import React, { useState, useEffect } from 'react';
import { Search, Plus, CreditCard, DollarSign, Calendar, Trash2, ChevronDown, ChevronUp, Bell } from 'lucide-react';
import AddPaymentPlanModal from './Modals/AddPaymentPlanModal';
import PaymentReminderModal from './Modals/PaymentReminderModal';
import { supabase } from '../../lib/supabase';

interface Installment {
    date: string;
    amount: number;
    status: 'pending' | 'paid';
    payment_method?: string;
}

interface PaymentPlan {
    id: string;
    total_amount: number;
    currency: string;
    installments: Installment[];
    student: {
        first_name: string;
        last_name: string;
        phone?: string;
        parent_phone?: string;
    };
    notes?: string;
    company_entity?: string;
    status: string;
}

const Payments: React.FC = () => {
    const [showAddModal, setShowAddModal] = useState(false);
    const [payments, setPayments] = useState<PaymentPlan[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedRows, setExpandedRows] = useState<string[]>([]);

    // Reminder State
    const [showReminderModal, setShowReminderModal] = useState(false);
    const [pendingReminders, setPendingReminders] = useState<any[]>([]);

    useEffect(() => {
        fetchPayments();
    }, []);

    const fetchPayments = async () => {
        try {
            setLoading(true);
            const { data, error } = await supabase
                .from('testprep_payments')
                .select(`
                    *,
                    student:testprep_students (
                        first_name,
                        last_name,
                        phone,
                        parent_phone
                    )
                `)
                .order('created_at', { ascending: false });

            if (error) throw error;
            setPayments(data || []);
        } catch (error) {
            console.error('Error fetching payments:', error);
        } finally {
            setLoading(false);
        }
    };

    const checkReminders = async () => {
        // 1. Fetch previously sent reminders to avoid duplicates
        const { data: history, error } = await supabase
            .from('testprep_payment_reminders')
            .select('*');

        if (error) {
            console.error('Error fetching reminder history:', error);
            alert('Geçmiş hatırlatmalar kontrol edilemedi.');
            return;
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const remindersToSend: any[] = [];

        payments.forEach(plan => {
            if (plan.status === 'paid' || plan.status === 'completed') return;

            plan.installments.forEach(inst => {
                if (inst.status !== 'pending') return;

                const dueDate = new Date(inst.date);
                dueDate.setHours(0, 0, 0, 0);

                const diffTime = dueDate.getTime() - today.getTime();
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                let type: '3_days' | '1_day' | 'same_day' | null = null;
                if (diffDays === 3) type = '3_days';
                if (diffDays === 1) type = '1_day';
                if (diffDays === 0) type = 'same_day';

                if (type) {
                    const alreadySent = history?.find(h =>
                        h.payment_plan_id === plan.id &&
                        h.reminder_type === type &&
                        h.installment_date === inst.date
                    );

                    if (!alreadySent) {
                        remindersToSend.push({
                            plan,
                            installment: inst,
                            type,
                            diffDays
                        });
                    }
                }
            });
        });

        setPendingReminders(remindersToSend);
        setShowReminderModal(true);
    };

    const toggleRow = (id: string) => {
        setExpandedRows(prev =>
            prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]
        );
    };

    const handleDelete = async (id: string) => {
        if (!await window.appConfirm('Bu ödeme planını silmek istediğinize emin misiniz?')) return;
        try {
            const { error } = await supabase
                .from('testprep_payments')
                .delete()
                .eq('id', id);

            if (error) throw error;
            setPayments(prev => prev.filter(p => p.id !== id));
        } catch (error: any) {
            alert('Silme işlemi başarısız: ' + error.message);
        }
    };

    // Update Overall Plan Status
    const handlePlanStatusChange = async (id: string, newStatus: string) => {
        const plan = payments.find(p => p.id === id);
        if (!plan) return;

        // If setting to 'paid', mark all installments as paid
        // If setting to 'pending', mark all as pending? (Maybe dangerous, let's just update the top level for now or ask logic)
        // User previous request was: "if paid selected, pay next installment". 
        // But now with expanded view, user might use the main dropdown less or for bulk actions.
        // Let's keep the "Pay Next" logic for the main dropdown, OR just keep it as a label if using expanded view.

        // For consistency with previous request:
        let updatedInstallments = [...plan.installments];
        let finalStatus = newStatus;

        if (newStatus === 'paid') {
            const nextIdx = updatedInstallments.findIndex(i => i.status === 'pending');
            if (nextIdx !== -1) {
                updatedInstallments[nextIdx] = { ...updatedInstallments[nextIdx], status: 'paid' };
                // If there are still pending ones, keep parent active
                if (updatedInstallments.some(i => i.status === 'pending')) {
                    finalStatus = 'active';
                }
            } else {
                finalStatus = 'paid';
            }
        }

        setPayments(prev => prev.map(p => p.id === id ? { ...p, status: finalStatus, installments: updatedInstallments } : p));

        try {
            const { error } = await supabase
                .from('testprep_payments')
                .update({
                    status: finalStatus,
                    installments: updatedInstallments
                })
                .eq('id', id);

            if (error) throw error;
        } catch (error: any) {
            console.error('Update failed:', error);
            fetchPayments();
        }
    };

    // Update Individual Installment Status
    const handleInstallmentStatusChange = async (planId: string, index: number, newStatus: 'paid' | 'pending', method?: string) => {
        const plan = payments.find(p => p.id === planId);
        if (!plan) return;

        const updatedInstallments = [...plan.installments];
        updatedInstallments[index] = { 
            ...updatedInstallments[index], 
            status: newStatus,
            payment_method: method 
        };

        // Auto-update parent status based on all installments
        const allPaid = updatedInstallments.every(i => i.status === 'paid');
        const parentStatus = allPaid ? 'paid' : 'active'; // or 'active' if partially paid

        // Optimistic update
        setPayments(prev => prev.map(p => p.id === planId ? { ...p, status: parentStatus, installments: updatedInstallments } : p));

        try {
            const { error } = await supabase
                .from('testprep_payments')
                .update({
                    installments: updatedInstallments,
                    status: parentStatus
                })
                .eq('id', planId);

            if (error) throw error;
        } catch (error: any) {
            console.error('Installment update failed:', error);
            fetchPayments();
        }
    };

    const calculateStats = (plan: PaymentPlan) => {
        const total = plan.total_amount;

        const paidInstallments = plan.installments
            .filter(i => i.status === 'paid')
            .reduce((acc, curr) => acc + Number(curr.amount), 0);

        const paid = paidInstallments;
        const remainingTotal = total - paid;

        const pendingInstallments = plan.installments
            .filter(i => i.status === 'pending')
            .sort((a, b) => a.date.localeCompare(b.date));

        const nextInstallment = pendingInstallments.length > 0 ? pendingInstallments[0] : null;

        return { total, paid, remainingTotal, nextInstallment };
    };

    const filteredPayments = payments.filter(p =>
        p.student.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.student.last_name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const totalCollected = payments.reduce((acc, p) => {
        const stats = calculateStats(p);
        let amountInEur = stats.paid;
        if (p.currency === 'TRY') amountInEur /= 35;
        else if (p.currency === 'USD') amountInEur /= 1.08;
        return acc + amountInEur;
    }, 0);

    const totalExpected = payments.reduce((acc, p) => {
        const stats = calculateStats(p);
        let amountInEur = stats.remainingTotal;
        if (p.currency === 'TRY') amountInEur /= 35;
        else if (p.currency === 'USD') amountInEur /= 1.08;
        return acc + amountInEur;
    }, 0);

    const totalOverdue = payments.filter(p => p.status === 'overdue').reduce((acc, p) => {
        const stats = calculateStats(p);
        let amountInEur = stats.remainingTotal;
        if (p.currency === 'TRY') amountInEur /= 35;
        else if (p.currency === 'USD') amountInEur /= 1.08;
        return acc + amountInEur;
    }, 0);

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Ödemeler</h2>
                    <p className="text-gray-600 dark:text-gray-400">
                        Öğrenci ödeme planları ve tahsilat takibi
                    </p>
                </div>
                <div className="flex space-x-2">
                    <button
                        onClick={checkReminders}
                        className="flex items-center space-x-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                        <Bell className="w-4 h-4" />
                        <span>Hatırlatıcı Kontrolü</span>
                    </button>
                    <button
                        onClick={() => setShowAddModal(true)}
                        className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                        <Plus className="w-4 h-4" />
                        <span>Yeni Ödeme Planı</span>
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-white dark:bg-gray-900 p-4 rounded-xl border border-gray-200 dark:border-gray-800">
                    <div className="flex items-center space-x-3 text-green-600 mb-2">
                        <DollarSign className="w-5 h-5" />
                        <span className="font-medium">Tahsil Edilen (Toplam)</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        {totalCollected.toLocaleString('tr-TR', { maximumFractionDigits: 0 })} €
                    </p>
                </div>
                <div className="bg-white dark:bg-gray-900 p-4 rounded-xl border border-gray-200 dark:border-gray-800">
                    <div className="flex items-center space-x-3 text-red-600 mb-2">
                        <Calendar className="w-5 h-5" />
                        <span className="font-medium">Gecikmiş Ödemeler</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        {totalOverdue.toLocaleString('tr-TR', { maximumFractionDigits: 0 })} €
                    </p>
                </div>
                <div className="bg-white dark:bg-gray-900 p-4 rounded-xl border border-gray-200 dark:border-gray-800">
                    <div className="flex items-center space-x-3 text-blue-600 mb-2">
                        <CreditCard className="w-5 h-5" />
                        <span className="font-medium">Beklenen (Genel)</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        {totalExpected.toLocaleString('tr-TR', { maximumFractionDigits: 0 })} €
                    </p>
                </div>
            </div>

            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden">
                <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex gap-4">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input
                            type="text"
                            placeholder="Öğrenci ara..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50 dark:bg-gray-800 text-left">
                            <tr>
                                <th className="py-3 px-6 text-xs font-medium text-gray-500 uppercase w-10"></th>
                                <th className="py-3 px-6 text-xs font-medium text-gray-500 uppercase">Öğrenci</th>
                                <th className="py-3 px-6 text-xs font-medium text-gray-500 uppercase">Şirket</th>
                                <th className="py-3 px-6 text-xs font-medium text-gray-500 uppercase">Toplam Tutar</th>
                                <th className="py-3 px-6 text-xs font-medium text-gray-500 uppercase">Ödenen</th>
                                <th className="py-3 px-6 text-xs font-medium text-gray-500 uppercase">Sıradaki Taksit</th>
                                <th className="py-3 px-6 text-xs font-medium text-gray-500 uppercase">Toplam Kalan</th>
                                <th className="py-3 px-6 text-xs font-medium text-gray-500 uppercase w-40">Durum</th>
                                <th className="py-3 px-6 text-xs font-medium text-gray-500 uppercase text-center w-24">İşlemler</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {loading ? (
                                <tr>
                                    <td colSpan={8} className="py-8 text-center">
                                        <div className="flex justify-center">
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                        </div>
                                    </td>
                                </tr>
                            ) : filteredPayments.length > 0 ? (
                                filteredPayments.map((plan) => {
                                    const stats = calculateStats(plan);
                                    const isExpanded = expandedRows.includes(plan.id);

                                    const nextDate = stats.nextInstallment ? new Date(stats.nextInstallment.date).toLocaleDateString('tr-TR') : null;
                                    const nextAmount = stats.nextInstallment ? stats.nextInstallment.amount : 0;

                                    return (
                                        <React.Fragment key={plan.id}>
                                            <tr className={`hover:bg-gray-50 dark:hover:bg-gray-800/50 ${isExpanded ? 'bg-gray-50 dark:bg-gray-800/50' : ''}`}>
                                                <td className="py-4 px-2 text-center">
                                                    {plan.installments.length > 0 && (
                                                        <button
                                                            onClick={() => toggleRow(plan.id)}
                                                            className="text-gray-400 hover:text-blue-600 transition-colors"
                                                        >
                                                            {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                                                        </button>
                                                    )}
                                                </td>
                                                <td className="py-4 px-6 text-sm font-medium text-gray-900 dark:text-white">
                                                    {plan.student.first_name} {plan.student.last_name}
                                                    <div className="text-xs text-gray-400 font-normal mt-0.5">
                                                        {plan.installments.length === 1 ? 'Peşin' : `${plan.installments.length} Taksit`}
                                                    </div>
                                                </td>
                                                <td className="py-4 px-6 text-sm text-gray-600 dark:text-gray-400">
                                                    {plan.company_entity || '-'}
                                                </td>
                                                <td className="py-4 px-6 text-sm text-gray-900 dark:text-white font-bold">
                                                    {plan.total_amount.toLocaleString('tr-TR')} {plan.currency}
                                                </td>
                                                <td className="py-4 px-6 text-sm text-green-600 font-medium">
                                                    {stats.paid.toLocaleString('tr-TR')} {plan.currency}
                                                </td>
                                                <td className="py-4 px-6 text-sm text-gray-500">
                                                    {nextDate ? (
                                                        <div>
                                                            <div className="font-semibold text-gray-700 dark:text-gray-300">
                                                                {Number(nextAmount).toLocaleString('tr-TR')} {plan.currency}
                                                            </div>
                                                            <div className="text-xs text-blue-600 font-medium bg-blue-50 dark:bg-blue-900/20 px-2 py-0.5 rounded-full inline-block mt-1 border border-blue-100 dark:border-blue-900/30">
                                                                {nextDate}
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <span className="text-gray-400 text-xs">-</span>
                                                    )}
                                                </td>
                                                <td className="py-4 px-6 text-sm text-red-600 font-medium">
                                                    {stats.remainingTotal.toLocaleString('tr-TR')} {plan.currency}
                                                </td>
                                                <td className="py-4 px-6">
                                                    <select
                                                        value={plan.status || 'active'}
                                                        onChange={(e) => handlePlanStatusChange(plan.id, e.target.value)}
                                                        // Slightly different style for main selector
                                                        className={`px-3 py-1 text-sm rounded-full border-0 cursor-pointer focus:ring-2 focus:ring-blue-500 bg-opacity-20 ${plan.status === 'paid' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                                                            plan.status === 'overdue' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' :
                                                                'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                                                            }`}
                                                    >
                                                        <option value="paid">{plan.installments.length === 1 ? 'Tahsil Et' : '1 Taksit Tahsil Et'}</option>
                                                        <option value="active">Aktif / Bekliyor</option>
                                                        <option value="overdue">Ödeme Yapılmadı</option>
                                                    </select>
                                                </td>
                                                <td className="py-4 px-6 text-center">
                                                    <div className="flex items-center justify-end space-x-2">
                                                        <button
                                                            onClick={() => handleDelete(plan.id)}
                                                            className="p-1.5 text-red-600 hover:text-red-800 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                                            title="Planı Sil"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                            {/* Sub-Rows for Installments */}
                                            {isExpanded && plan.installments.length > 0 && (
                                                <tr>
                                                    <td colSpan={8} className="bg-gray-50/50 dark:bg-gray-800/30 p-4">
                                                        <div className="max-w-4xl mx-auto">
                                                            <div className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">Taksit Detayları</div>
                                                            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                                                                <table className="w-full text-sm">
                                                                    <thead className="bg-gray-100 dark:bg-gray-800">
                                                                        <tr>
                                                                            <th className="py-2 px-4 text-left">Taksit No</th>
                                                                            <th className="py-2 px-4 text-left">Vade Tarihi</th>
                                                                            <th className="py-2 px-4 text-left">Tutar</th>
                                                                            <th className="py-2 px-4 text-left">Durum</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                                                                        {plan.installments.map((inst, idx) => (
                                                                            <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                                                                <td className="py-3 px-4 text-gray-600 dark:text-gray-400 font-medium">
                                                                                    {plan.installments.length === 1 ? 'Peşin Ödeme' : `${idx + 1}. Taksit`}
                                                                                </td>
                                                                                <td className="py-3 px-4 text-gray-900 dark:text-white">
                                                                                    {new Date(inst.date).toLocaleDateString('tr-TR')}
                                                                                </td>
                                                                                <td className="py-3 px-4 font-bold text-gray-900 dark:text-white">
                                                                                    {Number(inst.amount).toLocaleString('tr-TR')} {plan.currency}
                                                                                </td>
                                                                                <td className="py-3 px-4">
                                                                                    <select
                                                                                        value={inst.payment_method || inst.status}
                                                                                        onChange={(e) => {
                                                                                            const val = e.target.value;
                                                                                            if (val === 'pending') {
                                                                                                handleInstallmentStatusChange(plan.id, idx, 'pending', undefined);
                                                                                            } else {
                                                                                                handleInstallmentStatusChange(plan.id, idx, 'paid', val);
                                                                                            }
                                                                                        }}
                                                                                        className={`px-3 py-1 text-xs rounded-full border border-gray-200 cursor-pointer outline-none focus:border-blue-500 ${inst.status === 'paid'
                                                                                            ? 'bg-green-50 text-green-700 border-green-200'
                                                                                            : 'bg-white text-gray-700'
                                                                                            }`}
                                                                                    >
                                                                                        <option value="pending">Bekliyor</option>
                                                                                        <optgroup label="Nakit">
                                                                                            <option value="Nakit - Bireysel hesap">Bireysel hesap</option>
                                                                                            <option value="Nakit - Skyland ofis">Skyland ofis</option>
                                                                                            <option value="Nakit - Ankara ofis">Ankara ofis</option>
                                                                                            <option value="Nakit - Bursa Ofis">Bursa Ofis</option>
                                                                                            <option value="Nakit - İzmir Ofis">İzmir Ofis</option>
                                                                                            <option value="Nakit - Kadıköy Ofis">Kadıköy Ofis</option>
                                                                                            <option value="Nakit - Fethiye Ofis">Fethiye Ofis</option>
                                                                                        </optgroup>
                                                                                        <optgroup label="Kredi Kartı">
                                                                                            <option value="Kredi Kartı - Pisa Edu Moka">Pisa Edu Moka</option>
                                                                                            <option value="Kredi Kartı - Pisa Test Prep Moka">Pisa Test Prep Moka</option>
                                                                                        </optgroup>
                                                                                        <optgroup label="Şirket">
                                                                                            <option value="Şirket - Pisa Edu">Pisa Edu</option>
                                                                                            <option value="Şirket - Pisa Test Prep">Pisa Test Prep</option>
                                                                                        </optgroup>
                                                                                    </select>
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td className="py-8 px-6 text-center text-gray-500" colSpan={7}>
                                        Plan bulunamadı.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <AddPaymentPlanModal
                isOpen={showAddModal}
                onClose={() => setShowAddModal(false)}
                onSave={() => {
                    fetchPayments();
                    setShowAddModal(false);
                }}
            />

            <PaymentReminderModal
                isOpen={showReminderModal}
                onClose={() => setShowReminderModal(false)}
                reminders={pendingReminders}
                onComplete={() => {
                    // Could refresh reminders if needed
                }}
            />
        </div>
    );
};
export default Payments;
